import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Card from "../ui/Card";
import "../../style/CompaniesMapIframe.css";

import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// receiver "you are here" pin
const ReceiverIcon = L.divIcon({
  className: "leaflet-marker--receiver",
  iconSize: [18, 18], iconAnchor: [9, 9], popupAnchor: [0, -12],
  html: '<div class="receiver-pin"></div>',
});

// numbered company pin
function companyIcon(count, isSelected) {
  const size = isSelected ? 38 : 32;
  const cls = isSelected ? "cmi-pin cmi-pin--selected"
    : count === 0 ? "cmi-pin cmi-pin--empty"
    : "cmi-pin";
  const label = count || "·";
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
    html: `<div class="${cls}">${label}</div>`,
  });
}

// cluster layer managed imperatively via useMap
function ClusterMarkers({ markers, selectedCompanyId, onMarkerClick }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // remove old cluster group
    if (groupRef.current) {
      map.removeLayer(groupRef.current);
      groupRef.current = null;
    }

    if (!markers?.length) return;

    const group = L.markerClusterGroup({
      maxClusterRadius: 45,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const children = cluster.getAllChildMarkers();
        const total = children.reduce((sum, m) => sum + (m.options.listingCount || 0), 0);
        return L.divIcon({
          className: "",
          iconSize: [40, 40],
          iconAnchor: [20, 20],
          html: `<div class="cmi-cluster">${total}</div>`,
        });
      },
    });

    // build cluster tooltip on hover
    group.on("clustermouseover", (e) => {
      const children = e.layer.getAllChildMarkers();
      const names = [...new Set(children.map((m) => m.options.companyName).filter(Boolean))];
      const MAX_SHOW = 5;
      let label = names.slice(0, MAX_SHOW).join(", ");
      if (names.length > MAX_SHOW) label += ` +${names.length - MAX_SHOW} more`;
      e.layer.bindTooltip(label, { sticky: true, direction: "top", className: "cmi-tooltip" }).openTooltip();
    });
    group.on("clustermouseout", (e) => {
      try { e.layer.unbindTooltip(); } catch (_) {}
    });

    for (const m of markers) {
      if (!m?.position) continue;
      const count = m.listingCount || 0;
      const isSelected = selectedCompanyId != null
        && String(m.companyId) === String(selectedCompanyId);

      const marker = L.marker(m.position, {
        icon: companyIcon(count, isSelected),
        zIndexOffset: isSelected ? 1000 : 0,
        listingCount: count,
        companyName: m.companyName || "",
      });

      // hover tooltip
      const tip = count > 0
        ? `${m.companyName || "—"} · ${count} listing${count === 1 ? "" : "s"}`
        : m.companyName || "—";
      marker.bindTooltip(tip, { direction: "top", className: "cmi-tooltip" });

      marker.bindPopup(`
        <div style="max-width:220px">
          <div style="font-weight:700;margin-bottom:2px">${m.companyName || ""}</div>
          ${m.address ? `<div style="margin-bottom:4px;font-size:0.85em">${m.address}</div>` : ""}
          <div style="font-size:0.85em;color:#666">${count} ${count === 1 ? "listing" : "listings"}</div>
        </div>
      `);

      marker.on("click", () => onMarkerClick?.(m));
      group.addLayer(marker);
    }

    map.addLayer(group);
    groupRef.current = group;

    return () => {
      if (groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [map, markers, selectedCompanyId, onMarkerClick]);

  return null;
}

// auto-fit map to show receiver location + radius circle (priority),
// only including markers that fall within the radius
function FitBounds({ markers, receiverCoords, radiusKm }) {
  const map = useMap();
  const prevKey = useRef("");

  useEffect(() => {
    const rLat = receiverCoords?.lat;
    const rLng = receiverCoords?.lon ?? receiverCoords?.lng;

    const pts = [];

    // prioritize receiver + radius circle
    if (rLat != null && rLng != null) {
      pts.push([rLat, rLng]);
      if (radiusKm > 0) {
        const r = radiusKm / 111.32;
        const rLngDeg = r / Math.cos((rLat * Math.PI) / 180);
        pts.push([rLat + r, rLng], [rLat - r, rLng]);
        pts.push([rLat, rLng + rLngDeg], [rLat, rLng - rLngDeg]);
      }
    }

    // only add markers that are within the radius (so distant ones don't zoom out the view)
    if (Array.isArray(markers) && rLat != null && rLng != null && radiusKm > 0) {
      for (const m of markers) {
        if (!m?.position) continue;
        const dLat = m.position.lat - rLat;
        const dLng = m.position.lng - rLng;
        const dist = Math.sqrt(dLat * dLat + dLng * dLng) * 111.32;
        if (dist <= radiusKm * 1.2) pts.push([m.position.lat, m.position.lng]);
      }
    } else if (Array.isArray(markers) && pts.length === 0) {
      // no receiver coords — fall back to fitting all markers
      for (const m of markers) {
        if (m?.position) pts.push([m.position.lat, m.position.lng]);
      }
    }

    if (pts.length === 0) return;
    const key = pts.map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).join("|")
      + `|r${radiusKm || 0}`;
    if (key === prevKey.current) return;
    prevKey.current = key;

    map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 16, animate: true });
  }, [markers, receiverCoords, radiusKm, map]);

  return null;
}

function MapResizeHandler({ wrapperRef }) {
  const map = useMap();
  useEffect(() => {
    if (!wrapperRef?.current) return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(wrapperRef.current);
    setTimeout(() => map.invalidateSize(), 0);
    return () => ro.disconnect();
  }, [map, wrapperRef]);
  return null;
}

const CompaniesMapIframe = ({
  markers = [],
  center,
  zoom = 11,
  onMarkerClick,
  selectedCompanyId,
  receiverCoords,
  radiusKm,
  variant = "default",
  icon,
  iconColor,
  title = "",
  description = "",
  colorClass = "",
  className = "",
  darkMode = false,
  value,
  children,
  height = 400,
}) => {
  const { t } = useTranslation();
  const displayTitle = title === "" ? null : (title || t("CompaniesMap.title"));
  const wrapperRef = useRef(null);
  const initialCenter = useMemo(() => center || { lat: 45, lng: -73 }, [center]);

  return (
    <Card variant={variant} icon={icon} iconColor={iconColor} title={displayTitle}
      description={description} colorClass={colorClass} className={className}
      darkMode={darkMode} value={value}>
      <div ref={wrapperRef} className="companies-map__wrapper"
        style={{ width: "100%", minHeight: `${height}px`, height: `${height}px` }}>
        <div className="companies-map__iframe" style={{ width: "100%", height: "100%" }}>
          <MapContainer center={initialCenter} zoom={zoom}
            style={{ width: "100%", height: "100%" }} scrollWheelZoom>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />

            <FitBounds markers={markers} receiverCoords={receiverCoords} radiusKm={radiusKm} />
            <MapResizeHandler wrapperRef={wrapperRef} />

            <ClusterMarkers markers={markers} selectedCompanyId={selectedCompanyId}
              onMarkerClick={onMarkerClick} />

            {receiverCoords?.lat != null && (
              <Marker position={{ lat: receiverCoords.lat, lng: receiverCoords.lon ?? receiverCoords.lng }}
                icon={ReceiverIcon} zIndexOffset={-1000}>
                <Popup><span style={{ fontWeight: 600 }}>Your location</span></Popup>
              </Marker>
            )}

            {receiverCoords?.lat != null && radiusKm > 0 && (
              <Circle
                center={{ lat: receiverCoords.lat, lng: receiverCoords.lon ?? receiverCoords.lng }}
                radius={radiusKm * 1000}
                pathOptions={{ color: "#60a5fa", fillColor: "#60a5fa", fillOpacity: 0.10, weight: 2, dashArray: "6 4" }}
              />
            )}
          </MapContainer>
        </div>
      </div>
      {children}
    </Card>
  );
};

CompaniesMapIframe.propTypes = {
  markers: PropTypes.array,
  center: PropTypes.object,
  zoom: PropTypes.number,
  onMarkerClick: PropTypes.func,
  selectedCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  receiverCoords: PropTypes.object,
  radiusKm: PropTypes.number,
  variant: PropTypes.string,
  icon: PropTypes.object,
  iconColor: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  colorClass: PropTypes.string,
  className: PropTypes.string,
  darkMode: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CompaniesMapIframe;
